/* ---------------------------------------------------------------------------
   Site refresh 2026: button effects (see refresh-buttons.css).
   - Mouse: magnetic pull on every button, and the fill origin for the main
     calls to action. Listeners are delegated, so buttons rendered later
     (cart drawer, quick view) work too.
   - Any pointer, or Enter/Space: gold sparks from the main calls to action.
   Nothing runs when reduced motion is requested.
   --------------------------------------------------------------------------- */
(() => {
	const MAIN = ".slideshow__slide-button, .add-to-cart, .cart-footer__button, .contact__button, .rn-cta, .rn-drawer__cta, .rn-mega__card-button, .rs__btn:not(.rs__btn--ghost), .rco-btn--submit";
	/* 2026 pills and round arrow buttons (btn-fx): pull and pour, see refresh-buttons.css */
	const PILLS = ".rf__filter-btn, .rf__foot-show, .rf__foot-clear, .rg__all, .rcd-continue, .rp-sticky__atc, .rvf__submit, .rvf__browse, .rbp-lead__more, .rbp-more__link, .rbp-band__button, .rcl__button, .rcl__pill, .rit__btn, .rco-btn--ink, .rv__button, .rfa__all, .rfa-strip__all, .rfa-back, .rfa-order__button, .rf-footer__subscribe, .rs__btn, .rqb__btn, .rfa-card--lead .rfa-card__more";
	const ROUND = ".rch__nav-btn, .rbp-search__submit, .rv__arrow, .rp-gallery__btn, .rp-gallery__play, .rg-viewer__nav, .rg-viewer__close, .rv-viewer button";
	const ALL = MAIN + ", .button, .rn-mega__all, .article-card__read-more, " + PILLS + ", " + ROUND;
	const PULL_X = 0.22;
	const PULL_Y = 0.3;
	const MAX_X = 10;
	const MAX_Y = 6;

	const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
	const hover = window.matchMedia("(hover: hover) and (pointer: fine)");
	const root = document.documentElement;

	function sync() {
		root.classList.toggle("rf-btn-fx", hover.matches && !reduce.matches);
	}
	sync();
	[reduce, hover].forEach((query) => query.addEventListener && query.addEventListener("change", sync));

	function buttonFor(node) {
		const el = node && node.closest ? node.closest(ALL) : null;
		if (!el || el.disabled || el.classList.contains("disabled") || el.getAttribute("aria-disabled") === "true") {
			return null;
		}
		return el;
	}

	function setOrigin(el, event) {
		const box = el.getBoundingClientRect();
		el.style.setProperty("--rf-x", `${event.clientX - box.left}px`);
		el.style.setProperty("--rf-y", `${event.clientY - box.top}px`);
	}

	const clamp = (value, max) => Math.max(-max, Math.min(max, value));

	/* Capture phase: sliders (Swiper) stop pointer events from bubbling */
	const LISTEN = { capture: true, passive: true };

	/* Magnetic pull + fill origin (mouse only) */
	document.addEventListener("pointerover", (event) => {
		if (event.pointerType !== "mouse" || !root.classList.contains("rf-btn-fx")) {
			return;
		}
		const el = buttonFor(event.target);
		if (!el || el.contains(event.relatedTarget)) {
			return;
		}
		setOrigin(el, event);
		el.classList.add("rf-live");
	}, LISTEN);

	document.addEventListener("pointermove", (event) => {
		if (event.pointerType !== "mouse") {
			return;
		}
		const el = event.target.closest && event.target.closest(".rf-live");
		if (!el) {
			return;
		}
		const box = el.getBoundingClientRect();
		const dx = event.clientX - (box.left + box.width / 2);
		const dy = event.clientY - (box.top + box.height / 2);
		el.style.setProperty("--rf-mx", `${clamp(dx * PULL_X, MAX_X).toFixed(1)}px`);
		el.style.setProperty("--rf-my", `${clamp(dy * PULL_Y, MAX_Y).toFixed(1)}px`);
	}, LISTEN);

	document.addEventListener("pointerout", (event) => {
		const el = event.target.closest && event.target.closest(".rf-live");
		if (!el || el.contains(event.relatedTarget)) {
			return;
		}
		setOrigin(el, event);
		el.classList.remove("rf-live");
		el.style.removeProperty("--rf-mx");
		el.style.removeProperty("--rf-my");
	}, LISTEN);

	/* Gold sparks from the main calls to action */
	let layer = null;

	function sparks(x, y) {
		if (reduce.matches || !document.body) {
			return;
		}
		if (!layer || !layer.isConnected) {
			layer = document.createElement("div");
			layer.className = "rf-sparks";
			layer.setAttribute("aria-hidden", "true");
			document.body.appendChild(layer);
		}
		for (let i = 0; i < 12; i++) {
			const spark = document.createElement("span");
			spark.className = "rf-spark";
			spark.textContent = "✦";
			layer.appendChild(spark);

			const angle = (Math.PI * 2 * i) / 12 + Math.random() * 0.5;
			const distance = 40 + Math.random() * 36;
			const x0 = x - 7;
			const y0 = y - 8;
			const x1 = x0 + Math.cos(angle) * distance;
			const y1 = y0 + Math.sin(angle) * distance;

			const animation = spark.animate(
				[
					{ transform: `translate(${x0}px, ${y0}px) scale(0.4) rotate(0deg)`, opacity: 1 },
					{ transform: `translate(${x1}px, ${y1}px) scale(1.2) rotate(120deg)`, opacity: 0 }
				],
				{ duration: 700 + Math.random() * 200, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)", fill: "forwards" }
			);
			animation.onfinish = () => spark.remove();
		}
	}

	document.addEventListener("pointerdown", (event) => {
		if (event.button !== 0) {
			return;
		}
		const el = buttonFor(event.target);
		if (el && el.matches(MAIN)) {
			sparks(event.clientX, event.clientY);
		}
	}, LISTEN);

	/* Keyboard activation: sparks from the button's centre */
	document.addEventListener("click", (event) => {
		if (event.detail !== 0) {
			return;
		}
		const el = buttonFor(event.target);
		if (el && el.matches(MAIN)) {
			const box = el.getBoundingClientRect();
			sparks(box.left + box.width / 2, box.top + box.height / 2);
		}
	}, LISTEN);
})();
